/* ═══════════════════════════════════════════════════════════════════
   Codex Alarms — client-side alarm engine for the Aura Codex.

   • Extracts HH:MM times from user intents (e.g. "Gym · 06:00 AM")
   • Every second, checks whether any pillar has an alarm time matching
     the current minute.  If YES → opens a 5-MIN Fuse Window:
       - Fires an energetic 15-second Web Audio tone
       - Every minute (0, 1, 2, 3, 4) re-fires the tone
       - Emits `codex-alarm` CustomEvent so tiles can blink
   • If the corresponding tile is FUSED during the window → cancel.
   • If the 5-min window expires → auto-cancel.
   • Only ONE alarm can be active at a time (the first that matches).

   Web-preview + Playstore friendly:
     - Uses transform+opacity (no `filter`) for blinking so Android
       WebView doesn't drop frames.
     - Uses Web Audio API (already unlocked once user interacts).
     - Fires browser Notification when permission is granted (best effort
       for web builds; native APK will layer capacitor local-notifications
       on top of this same event bus).
   ═══════════════════════════════════════════════════════════════════ */

import { getAudioCtx } from './audioCore';

/* Parse an HH:MM (24-h) time out of any free-form intent text.
   Supports "06:30", "6:30 am", "10:15 PM", "at 22:00", etc.
   Returns { hh, mm } or null. */
export const parseAlarmTime = (text) => {
  if (!text) return null;
  const s = String(text);
  const m = s.match(/\b(\d{1,2})[:.](\d{2})\s*(am|pm)?\b/i);
  if (!m) return null;
  let hh = parseInt(m[1], 10);
  const mm = parseInt(m[2], 10);
  const ap = (m[3] || '').toLowerCase();
  if (hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;
  if (ap === 'pm' && hh < 12) hh += 12;
  if (ap === 'am' && hh === 12) hh = 0;
  return { hh, mm };
};

/* Compare two HH:MM pairs to see if the current time matches the alarm
   time within a small tolerance (within the same minute). */
export const timesMatch = (alarm, now) =>
  alarm && alarm.hh === now.getHours() && alarm.mm === now.getMinutes();

/* ── Signature energetic tone — 60-second continuous ANTHEM ──
   Full-minute rhythmic anthem so the tile blinks and rings for the
   SAME 60 seconds:
     • Kick drum on every beat (~120 BPM = 0.5 s/beat)
     • Punchy hi-hat noise on the off-beats
     • Rising bell melody (C major pentatonic) that climbs then falls
     • Sub-bass drone in the background */
/* Module-level handle to the currently-playing anthem so it can be
   killed the instant the user taps the STOP orb. Without this handle
   the audio context keeps ringing for the full 60 s even after the
   visual STOP toggle turns off. */
let __sigCtx = null;
let __sigMaster = null;
let __sigTimer = null;
let __hatBuf = null;

export const stopSignatureTone = () => {
  if (__sigTimer) { clearInterval(__sigTimer); __sigTimer = null; }
  const ctx = __sigCtx; const master = __sigMaster;
  __sigCtx = null; __sigMaster = null;
  if (!ctx || !master) return;
  try {
    const now = ctx.currentTime;
    master.gain.cancelScheduledValues(now);
    master.gain.setValueAtTime(Math.max(master.gain.value, 0.0001), now);
    master.gain.exponentialRampToValueAtTime(0.0001, now + 0.05);
  } catch (_) { /* ignore */ }
  setTimeout(() => { try { master.disconnect(); } catch (_) { /* ignore */ } }, 90);
};

/* 60-second anthem on the shared context. A 250 ms look-ahead scheduler
   creates only ~1 s of nodes at a time instead of ~1,000 nodes up-front,
   which used to stall the Android WebView audio thread. */
export const playSignatureTone = () => {
  stopSignatureTone();
  const ctx = getAudioCtx();
  if (!ctx) return;
  const totalDur = 60;
  const beat = 0.5;
  const beatCount = Math.floor(totalDur / beat);
  const t0 = ctx.currentTime + 0.05;

  const master = ctx.createGain();
  master.gain.setValueAtTime(0, t0);
  master.gain.linearRampToValueAtTime(0.9, t0 + 0.15);
  master.gain.setValueAtTime(0.9, t0 + totalDur - 0.6);
  master.gain.linearRampToValueAtTime(0, t0 + totalDur);
  master.connect(ctx.destination);
  __sigCtx = ctx; __sigMaster = master;

  const bassOsc = ctx.createOscillator();
  const bassG = ctx.createGain();
  bassOsc.type = 'triangle';
  bassOsc.frequency.setValueAtTime(65.41, t0);
  bassG.gain.setValueAtTime(0.14, t0);
  bassOsc.connect(bassG).connect(master);
  bassOsc.start(t0); bassOsc.stop(t0 + totalDur + 0.1);

  if (!__hatBuf || __hatBuf.sampleRate !== ctx.sampleRate) {
    __hatBuf = ctx.createBuffer(1, Math.floor(0.15 * ctx.sampleRate), ctx.sampleRate);
    const d = __hatBuf.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
  }

  const kick = (t) => {
    const osc = ctx.createOscillator(); const g = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(130, t);
    osc.frequency.exponentialRampToValueAtTime(45, t + 0.14);
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(0.38, t + 0.005);
    g.gain.exponentialRampToValueAtTime(0.0005, t + 0.25);
    osc.connect(g).connect(master);
    osc.start(t); osc.stop(t + 0.3);
  };
  const hat = (t, level = 0.09, dur = 0.06) => {
    const src = ctx.createBufferSource(); src.buffer = __hatBuf;
    const hp = ctx.createBiquadFilter(); hp.type = 'highpass'; hp.frequency.value = 7000;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(level, t + 0.003);
    g.gain.exponentialRampToValueAtTime(0.0005, t + dur);
    src.connect(hp).connect(g).connect(master);
    src.start(t); src.stop(t + dur + 0.02);
  };
  const bell = (freq, t, dur = 0.9, level = 0.25) => {
    const osc = ctx.createOscillator(); const harm = ctx.createOscillator();
    const g = ctx.createGain(); const harmG = ctx.createGain();
    osc.type = 'sine'; harm.type = 'sine';
    osc.frequency.setValueAtTime(freq, t);
    harm.frequency.setValueAtTime(freq * 3, t);
    harmG.gain.setValueAtTime(0.14, t);
    harm.connect(harmG).connect(g); osc.connect(g);
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(level, t + 0.006);
    g.gain.exponentialRampToValueAtTime(0.0005, t + dur);
    g.connect(master);
    osc.start(t); osc.stop(t + dur + 0.05);
    harm.start(t); harm.stop(t + dur + 0.05);
  };

  const scale = [523.25, 587.33, 659.25, 783.99, 880.00, 1046.5, 1174.66, 1318.5];
  let b = 0;
  const pump = () => {
    if (__sigMaster !== master) return;
    const horizon = ctx.currentTime + 1.0;
    while (b < beatCount && t0 + b * beat < horizon) {
      const at = t0 + b * beat;
      kick(at);
      hat(at + beat * 0.5);
      const barBeat = b % 8;
      if (barBeat % 2 === 0) {
        const step = Math.floor(b / 2) % scale.length;
        const noteIdx = (Math.floor(b / 8) % 2 === 0) ? step : scale.length - 1 - step;
        bell(scale[noteIdx], at, 0.75, 0.24);
      }
      if (barBeat === 3) bell(2093.0, at, 0.9, 0.15);
      b += 1;
    }
    if (b >= beatCount && __sigTimer) {
      clearInterval(__sigTimer); __sigTimer = null;
      setTimeout(() => { if (__sigMaster === master) { __sigCtx = null; __sigMaster = null; } }, (totalDur + 0.3) * 1000 - (ctx.currentTime - t0) * 1000);
    }
  };
  try { pump(); __sigTimer = setInterval(pump, 250); } catch (_) { /* ignore */ }
};

/* Fire a browser notification (best-effort on web / desktop). Native
   APK will register real @capacitor/local-notifications separately. */
export const fireBrowserNotification = (pillarLabel) => {
  try {
    if (typeof window === 'undefined') return;
    if (!('Notification' in window)) return;
    if (Notification.permission !== 'granted') return;
    // eslint-disable-next-line no-new
    new Notification(`Bliss AuraCodex · ${pillarLabel} Time`, {
      body: `Time for ${pillarLabel} — Fuse it in the ${pillarLabel} tile.`,
      tag: `codex-${pillarLabel}`,
    });
  } catch (_) { /* ignore */ }
};

/* Request notification permission on user gesture (safe to call
   repeatedly — no-op after first grant/deny). */
export const requestNotificationPermission = async () => {
  try {
    if (!('Notification' in window)) return 'unsupported';
    if (Notification.permission === 'granted') return 'granted';
    if (Notification.permission === 'denied')  return 'denied';
    return await Notification.requestPermission();
  } catch (_) { return 'error'; }
};
